/**
 * Module 'landing': Orgus's own landing queue and environments (architecture doc 4, 8.2,
 * 9 phase 3, 11.7).
 *
 * One serialised queue per repo x target branch. A landing takes a change (a branch) through
 * change -> verify -> push -> deploy -> landed: the queue rebases it onto the target in a fresh
 * checkout on an eligible machine, runs the project's verify command there (job.run), pushes
 * it (only the queue pushes to protected branches, never with force) and deploys it to the
 * environments that follow the branch. Environments keep deployment records with the version,
 * the landing it came from, promote and rollback. Browser-safe: no Node built-ins.
 */
import type { Id, Ts, UiErrorShape } from '../index.ts';
import { ulid } from './ids.ts';
import type { LandingBackend, LandingBackendChoice, LandingBackendPin } from './landing-backend.ts';

// ---------------------------------------------------------------------------
// Landings and queues
// ---------------------------------------------------------------------------

/** Board columns, left to right. */
export type LandingStage = 'change' | 'verify' | 'push' | 'deploy' | 'landed';
export const LANDING_STAGES: readonly { id: LandingStage; title: string }[] = [
  { id: 'change', title: 'Queued' },
  { id: 'verify', title: 'Verifying' },
  { id: 'push', title: 'Pushing' },
  { id: 'deploy', title: 'Deploying' },
  { id: 'landed', title: 'Landed' },
];

/**
 * queued: waiting its turn. active: the queue is working on it (one per queue).
 * parked: it needs a person (a second conflict, or no verify command). failed: verify or
 * deploy failed. landed: on the target branch. cancelled: someone took it out.
 */
export type LandingStatus = 'queued' | 'active' | 'parked' | 'failed' | 'landed' | 'cancelled';
export const LANDING_STATUS_LABEL: Record<LandingStatus, string> = {
  queued: 'Queued',
  active: 'In progress',
  parked: 'Needs you',
  failed: 'Failed',
  landed: 'Landed',
  cancelled: 'Cancelled',
};

/** Where the change lives. */
export interface LandingSource {
  branch: string;
  /** A checkout that has the branch, when it is not on origin yet (fetched read-only from there). */
  checkoutId?: Id;
  machineId?: Id;
  /** The commit the branch pointed at when it was submitted, when known. */
  sha?: string;
}

export type LandingEventTone = 'info' | 'ok' | 'warn' | 'error';

/** One line of a landing's timeline. */
export interface LandingEvent {
  at: Ts;
  stage: LandingStage;
  text: string;
  tone: LandingEventTone;
  machineId?: Id;
  jobId?: Id;
}

export interface Landing {
  /** lnd_<ulid> */
  id: Id;
  queueId: string;
  projectId: Id;
  /** The target branch, for example main. */
  target: string;
  title: string;
  source: LandingSource;
  submittedBy: string;
  /** A person sent it (the console, the phone), not a session or the integrator: they wait for its result. */
  byPerson?: boolean;
  submittedAt: Ts;
  missionId?: Id;
  sessionId?: Id;
  /** ORGU-299: Watch incident linked to this landing (e.g. revert for main-red). */
  incidentId?: Id;
  status: LandingStatus;
  stage: LandingStage;
  /** What is happening now, in words ("Running pnpm test on studio"). */
  step: string;
  /** The machine verifying it (or that last did). */
  machineId: Id | null;
  /** The running or last job (verify or deploy): its log is job:<jobId>. */
  jobId: Id | null;
  /** Verify runs so far. */
  attempts: number;
  /** Work folders prepared so far, across restarts: each prepare gets a new folder name. */
  rounds?: number;
  /** Conflicts so far: a rebase that did not apply, or a push the target refused because it moved. */
  conflicts: number;
  /** The target commit it was rebased onto. */
  baseSha: string | null;
  /** The rebased change that was verified. */
  headSha: string | null;
  /** The commit on the target once pushed. */
  landedSha: string | null;
  version: string | null;
  conflictFiles?: string[];
  /** Why it failed or parked, and what to do next. */
  problem?: UiErrorShape;
  startedAt: Ts | null;
  endedAt: Ts | null;
  updatedAt: Ts;
  /** How long verify took, last run. */
  verifyMs?: number;
  /** Deployments this landing started. */
  deploymentIds?: Id[];
  /** Newest last, capped at LANDING_EVENTS_MAX. */
  events: LandingEvent[];
  /** ADR 0077: the backend it went through; absent means the local queue (older records too). */
  backend?: LandingBackend;
  /** ADR 0077 `provider`: its pull request and entry in the host's merge queue. */
  host?: LandingHostEntry;
  /** R-FN-7: the tests the last verify ran and why (blast radius), decided on the node. Absent on older records. */
  selection?: LandingSelection;
}

/** One test file a landing runs, and why the change reaches it. */
export interface LandingSelectedTest {
  /** Repo path of the test file (for Go, the package). */
  id: string;
  /** Why it runs ("imports src/a.ts", "changed"). */
  reasons: string[];
}

/**
 * R-FN-7 / ADR 0111: what the landing engine chose for one verify, on the node holding the
 * checkout. `narrowed` runs `command` (the verify command limited to `tests`); `full` runs the
 * configured verify command whole, and `fullReasons` says why (unknown stack, config changed, ...).
 */
export interface LandingSelection {
  mode: 'narrowed' | 'full';
  /** The stack adapters that judged it ("typescript", "python", "go"); empty when none knew it. */
  stacks: string[];
  tests: LandingSelectedTest[];
  fullReasons: string[];
  /** The command that ran. */
  command: string;
  /** Files changed since the base commit, as counted. */
  changed: number;
}

/** hub -> agent 'select': choose the tests of a prepared change (runs the landing engine node-side). */
export interface LandingSelectRequest {
  workId: string;
  baseSha: string;
  /** The project's verify command, with {base} applied. */
  verifyCommand: string;
}

export type LandingSelectResult = { ok: true; selection: LandingSelection } | { ok: false; message: string };

/** Where a pull request stands in the host's queue (ADR 0077), as the landing shows it. */
export type LandingHostState = 'queued' | 'checking' | 'merged' | 'removed' | 'failed';

export interface LandingHostCheck {
  name: string;
  /** The host's own log link; '' when the check gave none. */
  url: string;
}

/** A `provider` landing's pull request and merge queue entry on the host. */
export interface LandingHostEntry {
  provider: 'github';
  /** The CI connection whose credential opens, enqueues and reads it. */
  connectionId: Id;
  /** owner/name. */
  repo: string;
  number: number;
  /** The pull request on the host. */
  url: string;
  /** The branch's merge queue, or auto-merge where the branch has none. */
  via?: 'merge-queue' | 'auto-merge';
  state: LandingHostState;
  /** 1 is next to merge. */
  position?: number;
  failedChecks: LandingHostCheck[];
  /** The commit Orgus pushed to the branch and enqueued. */
  headSha: string;
  /** When Orgus last read the entry. */
  checkedAt: Ts;
}

export const LANDING_EVENTS_MAX = 60;
/** Finished landings kept per queue. */
export const LANDING_RECENT_MAX = 50;
/** The second conflict parks a landing. */
export const LANDING_MAX_CONFLICTS = 2;

/** A machine that waits for its person's yes to land a project (LandingQueue.consent). */
export interface LandingConsentWait {
  machineId: Id;
  machineName: string;
  checkoutId: Id;
  /** asking: the question is on that screen; declined: nobody said yes; no-dialog: it has no screen. */
  state: 'asking' | 'declined' | 'no-dialog' | 'offline';
  askedAt: Ts;
  /** For declined and no-dialog: what to do next, in plain words. */
  next?: string;
}

export interface LandingQueue {
  /** `${projectId}:${target}` */
  id: string;
  projectId: Id;
  target: string;
  paused: boolean;
  pausedBy?: string;
  pausedAt?: Ts;
  /** Queued landing ids in landing order (the active one is not in it). */
  order: Id[];
  activeId: Id | null;
  /** Why the next landing is not starting, when it is not ("No eligible machine: ..."). */
  waiting?: string | null;
  /**
   * Machines that wait for their person to allow landing for this project (landing consent,
   * 1 Nov 2026): Orgus asked on each; the console offers "Allow landing on <machine>" to ask again.
   */
  consent?: LandingConsentWait[] | null;
  /**
   * ADR 0077: where this queue's landings go and why, one line for the header ("Lands through
   * GitHub merge queue"). Absent until Orgus first read the facts.
   */
  backend?: LandingBackendChoice & { checkedAt: Ts };
  /** Mean duration of the recent landings, for ETAs; null until one landed. */
  avgMs: number | null;
  landedCount: number;
  createdAt: Ts;
}

export function landingQueueId(projectId: Id, target: string): string {
  return `${projectId}:${target}`;
}

export function newLandingId(now?: number): Id {
  return `lnd_${ulid(now)}`;
}

export function newDeploymentId(now?: number): Id {
  return `dpl_${ulid(now)}`;
}

export function newFreezeId(now?: number): Id {
  return `frz_${ulid(now)}`;
}

/** The version a landed commit gets: its short sha. Exact, and what production confirmations name. */
export function versionOf(sha: string): string {
  return sha.slice(0, 7);
}

/** A queued landing's place and when it should land. */
export interface LandingEta {
  /** 1 = next. */
  position: number;
  /** Expected landing time; null until the queue has landed something to measure. */
  etaAt: Ts | null;
}

/**
 * Position and ETA of every queued landing, and of the active one (position 0). Each landing
 * takes the queue's mean; the active one has the mean minus what it has already used.
 */
export function queueEtas(q: Pick<LandingQueue, 'order' | 'activeId' | 'avgMs' | 'paused'>, landings: Record<Id, Pick<Landing, 'startedAt'>>, now: Ts): Record<Id, LandingEta> {
  const out: Record<Id, LandingEta> = {};
  const avg = q.avgMs;
  let t = now;
  if (q.activeId) {
    const a = landings[q.activeId];
    const used = a?.startedAt != null ? now - a.startedAt : 0;
    const left = avg == null ? null : Math.max(avg * 0.1, avg - used);
    out[q.activeId] = { position: 0, etaAt: left == null ? null : now + left };
    if (left != null) t = now + left;
  }
  q.order.forEach((id, i) => {
    out[id] = { position: i + 1, etaAt: avg == null || q.paused ? null : t + avg * (i + 1) };
  });
  return out;
}

// ---------------------------------------------------------------------------
// Configuration, environments, freezes, deployments
// ---------------------------------------------------------------------------

export type EnvironmentKind = 'preview' | 'staging' | 'production';
export const ENVIRONMENT_KINDS: readonly EnvironmentKind[] = ['preview', 'staging', 'production'];
export const ENVIRONMENT_KIND_LABEL: Record<EnvironmentKind, string> = { preview: 'Preview', staging: 'Staging', production: 'Production' };

/** A time deploys to an environment are refused. Either a one-off window or a weekly one. */
export interface DeployFreezeWindow {
  id: Id;
  reason: string;
  createdBy: string;
  createdAt: Ts;
  from?: Ts;
  to?: Ts;
  /** Every week on these days (0 = Sunday), from start to end ('HH:MM', 24 h) in time zone tz. */
  weekly?: { days: number[]; start: string; end: string; tz: string };
}

export interface EnvironmentConfig {
  /** Unique within the project, lower case: 'preview', 'staging', 'production', 'eu-production'. */
  name: string;
  kind: EnvironmentKind;
  /** Landings to this branch deploy here on their own. Null: only promote and rollback. */
  autoDeployFrom: string | null;
  /** Overrides the project's deploy command. Placeholders: {env} {version} {sha} {branch}. */
  deployCommand?: string;
  /** Used for rollback instead of the deploy command, when set. */
  rollbackCommand?: string;
  /** Deploys need an admin and a confirmation that names the exact version. Production always is. */
  protected: boolean;
  freezes: DeployFreezeWindow[];
}

export interface LandingProjectConfig {
  projectId: Id;
  /** Run in the rebased checkout; exit 0 lands the change. Empty: landings park until it is set. */
  verifyCommand: string;
  /** R-FN-7: false runs the verify command whole every time; absent or true runs only the tests the change reaches when the node can tell. */
  selectTests?: boolean;
  verifyTimeoutMin: number;
  /** Placeholders: {env} {version} {sha} {branch}. Empty: nothing deploys. */
  deployCommand: string;
  /** Machines that verify and deploy must carry every one of these labels. */
  machineLabels: string[];
  /** Only the queue may push here. Empty: the project's default branch and every queue target. */
  protectedBranches: string[];
  environments: EnvironmentConfig[];
  updatedBy: string;
  updatedAt: Ts;
  /** ADR 0077 `landing.backend`: a fixed backend, or 'auto' (the default, also when absent). */
  backend?: LandingBackendPin;
  /** ORGU-293: automatic deploys of this project per UTC day; past it, green landings are held. Absent or 0: no cap. */
  maxDeploysPerDay?: number;
  /**
   * ADR 0066: nobody saved settings, so Orgus filled them in from the repository: the verify
   * command and where each step came from. Gone once someone saves.
   */
  detected?: { evidence: string };
}

/** Body of PUT /api/landing/config/:projectId. */
export type LandingProjectConfigInput = Partial<Omit<LandingProjectConfig, 'projectId' | 'updatedBy' | 'updatedAt' | 'environments'>> & {
  environments?: (Omit<EnvironmentConfig, 'freezes'> & { freezes?: DeployFreezeWindow[] })[];
};

export const DEFAULT_VERIFY_TIMEOUT_MIN = 30;

/** The starting configuration of a project: three environments, staging following the default branch. */
/** A project's settings as the queue uses them: saved, else the defaults with the detected verify command (ADR 0066). */
export function effectiveLandingConfig(state: Pick<LandingState, 'configs' | 'detected'> | undefined, projectId: Id, defaultBranch: string): LandingProjectConfig {
  const saved = state?.configs.find((c) => c.projectId === projectId);
  if (saved) return saved;
  const c = defaultLandingConfig(projectId, defaultBranch, 0);
  const d = state?.detected?.[projectId];
  return d ? { ...c, verifyCommand: d.command, detected: { evidence: d.evidence } } : c;
}

export function defaultLandingConfig(projectId: Id, defaultBranch: string, now: Ts): LandingProjectConfig {
  return {
    projectId,
    verifyCommand: '',
    verifyTimeoutMin: DEFAULT_VERIFY_TIMEOUT_MIN,
    deployCommand: '',
    machineLabels: [],
    protectedBranches: [],
    environments: [
      { name: 'preview', kind: 'preview', autoDeployFrom: null, protected: false, freezes: [] },
      { name: 'staging', kind: 'staging', autoDeployFrom: defaultBranch, protected: false, freezes: [] },
      { name: 'production', kind: 'production', autoDeployFrom: null, protected: true, freezes: [] },
    ],
    updatedBy: 'orgus',
    updatedAt: now,
  };
}

export type DeploymentStatus = 'deploying' | 'live' | 'failed' | 'superseded' | 'rolled-back' | 'held';
export const DEPLOYMENT_STATUS_LABEL: Record<DeploymentStatus, string> = {
  deploying: 'Deploying',
  live: 'Live',
  failed: 'Failed',
  superseded: 'Replaced',
  'rolled-back': 'Rolled back',
  held: 'Held',
};

export type DeploymentKind = 'deploy' | 'promote' | 'rollback';

export interface Deployment {
  /** dpl_<ulid> */
  id: Id;
  projectId: Id;
  env: string;
  version: string;
  sha: string;
  /** The landing that produced this version, when known. */
  landingId: Id | null;
  kind: DeploymentKind;
  /** promote: the environment the version came from. */
  fromEnv?: string;
  /** rollback: the deployment it replaced. */
  rollbackOf?: Id;
  status: DeploymentStatus;
  by: string;
  startedAt: Ts;
  endedAt: Ts | null;
  machineId: Id | null;
  jobId: Id | null;
  problem?: UiErrorShape;
}

/** Placeholders in deploy commands. */
export function renderCommand(template: string, vars: { env: string; version: string; sha: string; branch: string }): string {
  return template.replace(/\{(env|version|sha|branch)\}/g, (_, k: keyof typeof vars) => vars[k]);
}

/** The local weekday and minutes of the day of `at` in time zone `tz`. */
function localParts(at: Ts, tz: string): { day: number; minutes: number } | null {
  try {
    const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    const parts = f.formatToParts(new Date(at));
    const wd = parts.find((p) => p.type === 'weekday')?.value ?? '';
    const h = Number(parts.find((p) => p.type === 'hour')?.value);
    const m = Number(parts.find((p) => p.type === 'minute')?.value);
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(wd);
    if (day < 0 || !Number.isFinite(h) || !Number.isFinite(m)) return null;
    return { day, minutes: h * 60 + m };
  } catch {
    return null;
  }
}

export function parseClock(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 24 && min < 60 && h * 60 + min <= 24 * 60 ? h * 60 + min : null;
}

/** True while the window is in force. A weekly window whose end is before its start runs past midnight. */
export function freezeActive(w: DeployFreezeWindow, at: Ts): boolean {
  if (w.weekly) {
    const p = localParts(at, w.weekly.tz || 'UTC');
    const start = parseClock(w.weekly.start);
    const end = parseClock(w.weekly.end);
    if (!p || start == null || end == null) return false;
    if (start <= end) return w.weekly.days.includes(p.day) && p.minutes >= start && p.minutes < end;
    // Overnight: from start on a listed day to end the next day.
    if (w.weekly.days.includes(p.day) && p.minutes >= start) return true;
    return w.weekly.days.includes((p.day + 6) % 7) && p.minutes < end;
  }
  return w.from != null && w.to != null && at >= w.from && at < w.to;
}

/** The freeze window in force on an environment now, if any. */
export function activeFreeze(env: Pick<EnvironmentConfig, 'freezes'>, at: Ts): DeployFreezeWindow | null {
  return env.freezes.find((w) => freezeActive(w, at)) ?? null;
}

/** When a window in force at `at` ends (a weekly one: today's end in its time zone, DST aside). */
export function freezeUntil(w: DeployFreezeWindow, at: Ts): Ts | null {
  if (!w.weekly) return w.to ?? null;
  const p = localParts(at, w.weekly.tz || 'UTC');
  const end = parseClock(w.weekly.end);
  if (!p || end == null) return null;
  const left = (((end - p.minutes) % 1440) + 1440) % 1440;
  return Math.floor(at / 60_000) * 60_000 + left * 60_000;
}

const utcMinute = (t: Ts) => `${new Date(t).toISOString().slice(0, 16).replace('T', ' ')} UTC`;

/**
 * ORGU-293: why an automatic deploy to `env` waits now, or null when it may run. In order: a
 * freeze window, a red main, the project's daily cap (`deployedToday` counts deploys that ran
 * this UTC day). The words are what the project shows.
 */
export function deployHold(o: { env: Pick<EnvironmentConfig, 'freezes'>; at: Ts; mainRed: boolean; maxPerDay?: number; deployedToday: number }): string | null {
  const f = activeFreeze(o.env, o.at);
  if (f) {
    const until = freezeUntil(f, o.at);
    return `held: freeze window ${f.reason}${until != null ? ` until ${utcMinute(until)}` : ''}`;
  }
  if (o.mainRed) return 'held: main is red';
  if (o.maxPerDay && o.maxPerDay > 0 && o.deployedToday >= o.maxPerDay) return `held: daily limit ${o.maxPerDay} reached`;
  return null;
}

/** Deploys of a project that ran (not held) on the UTC day of `at`. */
export function deploysToday(deployments: readonly Deployment[], projectId: Id, at: Ts): number {
  const day = new Date(at).toISOString().slice(0, 10);
  return deployments.filter((d) => d.projectId === projectId && d.kind === 'deploy' && d.status !== 'held' && new Date(d.startedAt).toISOString().slice(0, 10) === day).length;
}

/** "Fridays 16:00 to 23:59 (Europe/London)" or "12 Oct 09:00 to 13 Oct 09:00". */
export function describeFreeze(w: DeployFreezeWindow): string {
  if (w.weekly) {
    const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const days = [...w.weekly.days].sort((a, b) => a - b).map((d) => names[d]).join(', ');
    return `${days || 'No days'} ${w.weekly.start} to ${w.weekly.end} (${w.weekly.tz})`;
  }
  const fmt = (t: Ts | undefined) => (t == null ? '?' : new Date(t).toISOString().slice(0, 16).replace('T', ' '));
  return `${fmt(w.from)} to ${fmt(w.to)} UTC`;
}

/** An environment as the Environments page shows it. */
export interface EnvironmentView {
  projectId: Id;
  config: EnvironmentConfig;
  current: Deployment | null;
  /** What a rollback would go back to. */
  previous: Deployment | null;
  /** Running now. */
  deploying: Deployment | null;
  frozen: DeployFreezeWindow | null;
}

/** Current, previous and in-flight deployments of one environment, from the deployment records (newest first or any order). */
export function environmentView(projectId: Id, config: EnvironmentConfig, deployments: Deployment[], at: Ts): EnvironmentView {
  const mine = deployments.filter((d) => d.projectId === projectId && d.env === config.name).sort((a, b) => b.startedAt - a.startedAt);
  const current = mine.find((d) => d.status === 'live') ?? null;
  // What a rollback goes back to: the newest earlier version that was replaced normally, never
  // one that was itself rolled back afterwards (that is the version people just got away from).
  const rolledBackLater = (d: Deployment) => mine.some((x) => x.status === 'rolled-back' && x.sha === d.sha && x.startedAt >= d.startedAt);
  const previous = current ? (mine.find((d) => d.startedAt < current.startedAt && d.status === 'superseded' && d.sha !== current.sha && !rolledBackLater(d)) ?? null) : null;
  return {
    projectId,
    config,
    current,
    previous,
    deploying: mine.find((d) => d.status === 'deploying') ?? null,
    frozen: activeFreeze(config, at),
  };
}

// ---------------------------------------------------------------------------
// Push protection: only the queue pushes to protected branches
// ---------------------------------------------------------------------------

/** One git push in a command line. */
export interface PushCommand {
  /** Destination branches: '' is the current branch, '*' is every branch (--all, --mirror, a glob, a matching push). */
  targets: string[];
  /** Folders the push runs in, in order, each relative to the one before: `cd` and `git -C` arguments. */
  dirs: string[];
  /** A folder change Orgus cannot follow (`cd -`, `cd ~`, a variable, popd): the folder is unknown. */
  lost: boolean;
}

export interface PushAnalysis {
  pushes: PushCommand[];
  /**
   * The command mentions git and push in a way this reader cannot follow: inside `bash -c`,
   * `env`, `$( )`, backticks, a pipe into a shell, an alias set with -c. Callers fail closed.
   */
  opaque: boolean;
}

const MENTIONS_PUSH = /\bgit(?:\.exe)?\b[\s\S]*\bpush\b/i;
/** Words that only prefix the real command. */
const PREFIX_WORDS = new Set(['then', 'do', 'else', 'elif', 'if', 'while', 'until', '!', '{', '}', 'time', 'nohup', 'command', 'exec', 'builtin', 'noglob', 'call', 'sudo', 'doas', '&']);
/** Commands that print their arguments and run nothing. */
const PRINTERS = new Set(['echo', 'printf', 'write-host', 'write-output', ':']);
/** Commands that run a string or stdin as code. */
const RUNNERS = /^(sh|bash|zsh|dash|ksh|fish|cmd|powershell|pwsh|iex|invoke-expression|eval|source|\.|xargs|python3?|node|perl|ruby|start-process|start|wsl)(\.exe)?$/i;
const CD_WORDS = new Set(['cd', 'chdir', 'pushd', 'set-location', 'sl', 'push-location']);
/** git options before the subcommand that take the next word as their value. */
const GIT_VALUE_OPTS = new Set(['-C', '-c', '--git-dir', '--work-tree', '--namespace', '--config-env', '--super-prefix']);
/** git push options that take the next word as their value. */
const PUSH_VALUE_OPTS = new Set(['-o', '--push-option', '--repo', '--receive-pack', '--exec']);

type Piece = string;

/** Split a command line on ; & && | || newlines and parentheses, outside quotes. Parentheses come back as '(' and ')'. */
function pieces(command: string): Piece[] {
  // Line continuations (bash \ and PowerShell `) join lines.
  const src = command.replace(/[\\`]\r?\n/g, ' ');
  const out: Piece[] = [];
  let cur = '';
  let q: string | null = null;
  const flush = () => {
    if (cur.trim()) out.push(cur);
    cur = '';
  };
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (q) {
      if (ch === q) q = null;
      cur += ch;
      continue;
    }
    if (ch === '"' || ch === "'") {
      q = ch;
      cur += ch;
      continue;
    }
    if (ch === '&' && (src[i - 1] === '>' || src[i - 1] === '<' || src[i + 1] === '>')) {
      cur += ch; // a redirection: 2>&1, &>file
      continue;
    }
    if (ch === ';' || ch === '&' || ch === '|' || ch === '\n' || ch === '\r') {
      flush();
      continue;
    }
    if (ch === '(' || ch === ')') {
      flush();
      out.push(ch);
      continue;
    }
    cur += ch;
  }
  flush();
  return out;
}

/** The words of one simple command, quotes removed, redirections dropped. */
function words(part: string): string[] {
  const raw: { w: string; quoted: boolean }[] = [];
  for (const m of part.matchAll(/(?:[^\s"']+|"[^"]*"?|'[^']*'?)+/g)) raw.push({ w: m[0].replace(/["']/g, ''), quoted: /["']/.test(m[0]) });
  const out: string[] = [];
  for (let i = 0; i < raw.length; i++) {
    const { w, quoted } = raw[i]!;
    const r = quoted ? null : /^(?:\d+|&)?(?:>>?|<<?)&?(.*)$/.exec(w);
    if (r) {
      if (!r[1]) i++; // `> file`: the next word is the file
      continue;
    }
    out.push(w);
  }
  return out;
}

const unknownDir = (d: string) => !d || d === '-' || d.startsWith('~') || /[$%]/.test(d);

/** A refspec's destination branch: '' for the current branch, '*' for every branch, null for none (a tag). */
function refTarget(refspec: string): string | null {
  const spec = refspec.replace(/^\+/, '');
  if (spec === ':') return '*'; // matching branches
  const i = spec.indexOf(':');
  let name = i >= 0 ? spec.slice(i + 1) : spec;
  if (!name) name = spec.slice(0, i);
  if (/^(HEAD|@)([~^@:]|$)/.test(name)) return '';
  if (name.startsWith('refs/tags/')) return null;
  name = name.replace(/^refs\/heads\//, '').replace(/^heads\//, '');
  if (name.includes('*')) return '*';
  return name;
}

/** The push of one simple command whose command word is `w[i]` (git), or null when it is not a push. */
function readGitPush(w: string[], i: number, dirs: string[], lostIn: boolean): { push: PushCommand | null; opaque: boolean } {
  let lost = lostIn;
  const here = [...dirs];
  i++;
  while (i < w.length && w[i]!.startsWith('-')) {
    const o = w[i]!;
    const eq = o.startsWith('--') ? o.indexOf('=') : -1;
    const name = eq > 0 ? o.slice(0, eq) : o;
    const value = eq > 0 ? o.slice(eq + 1) : GIT_VALUE_OPTS.has(o) ? (w[++i] ?? '') : '';
    if (name === '-C' || name === '--work-tree' || name === '--git-dir') {
      // --git-dir=repo/.git: the work folder is repo.
      const d = name === '--git-dir' ? value.replace(/[\\/]?\.git[\\/]?$/i, '') || '.' : value;
      if (unknownDir(d)) lost = true;
      else here.push(d);
    }
    if ((name === '-c' || name === '--config-env') && /alias\.|push/i.test(value)) return { push: null, opaque: true };
    i++;
  }
  if (w[i] !== 'push') return { push: null, opaque: false };
  const args = w.slice(i + 1);
  const positional: string[] = [];
  let every = false;
  let tagsOnly = false;
  let repoGiven = false;
  let rest = false;
  for (let j = 0; j < args.length; j++) {
    const a = args[j]!;
    if (rest || !a.startsWith('-') || a === '-') {
      positional.push(a);
      continue;
    }
    if (a === '--') {
      rest = true;
      continue;
    }
    const name = a.includes('=') ? a.slice(0, a.indexOf('=')) : a;
    if (name === '--all' || name === '--mirror' || name === '--branches' || name === '--prune') every = true;
    else if (name === '--tags') tagsOnly = true;
    if (name === '--repo') repoGiven = true;
    if (PUSH_VALUE_OPTS.has(a)) j++;
  }
  const targets: string[] = [];
  // With --repo, git may read the first word as the remote or as a refspec: count it both ways.
  const refspecs = repoGiven ? positional : positional.slice(1);
  if (every) targets.push('*');
  for (const r of refspecs) {
    const t = refTarget(r);
    if (t != null) targets.push(t);
  }
  // No refspec: the current branch (or its upstream), unless only tags go.
  if ((!refspecs.length && !every && !tagsOnly) || repoGiven) targets.push('');
  return { push: { targets: [...new Set(targets)], dirs: here, lost }, opaque: false };
}

/**
 * Every git push in a shell command line, and the folder each runs in. Not a full shell
 * parser: it splits on ; & && | || newlines and parentheses (outside quotes), follows `cd`
 * and `git -C`, and reports anything else that mentions git push as opaque, so a caller can
 * fail closed.
 */
export function analyzePush(command: string): PushAnalysis {
  const pushes: PushCommand[] = [];
  if (!MENTIONS_PUSH.test(command)) return { pushes, opaque: false };
  // Command substitution runs code Orgus does not read.
  let opaque = /\$\(|`(?!\r?\n)|<\(/.test(command);
  const stack: { dirs: string[]; lost: boolean }[] = [];
  let dirs: string[] = [];
  let lost = false;
  for (const p of pieces(command)) {
    if (p === '(') {
      stack.push({ dirs: [...dirs], lost });
      continue;
    }
    if (p === ')') {
      const top = stack.pop();
      if (top) ({ dirs, lost } = top);
      continue;
    }
    const w = words(p);
    let i = 0;
    while (i < w.length && (/^[A-Za-z_][A-Za-z0-9_]*=/.test(w[i]!) || PREFIX_WORDS.has(w[i]!.toLowerCase()))) i++;
    const cmd = w[i] ?? '';
    if (!cmd || cmd.startsWith('#')) continue;
    const base = cmd.replace(/^.*[\\/]/, '').toLowerCase();
    if (CD_WORDS.has(base)) {
      const arg = w.slice(i + 1).find((x) => !/^(-[LPe@]|\/d|-path|-literalpath)$/i.test(x));
      if (arg == null || unknownDir(arg)) lost = true;
      else dirs = [...dirs, arg];
      continue;
    }
    if (base === 'popd' || base === 'pop-location') {
      lost = true;
      continue;
    }
    if (/^git(\.exe)?$/.test(base)) {
      const r = readGitPush(w, i, dirs, lost);
      if (r.opaque) opaque = true;
      if (r.push) pushes.push(r.push);
      continue;
    }
    if (RUNNERS.test(base) || (!PRINTERS.has(base) && MENTIONS_PUSH.test(p))) opaque = true;
  }
  return { pushes, opaque };
}

/**
 * The branches a shell command line pushes to. `git push origin feature:main` gives ['main'];
 * a push without a refspec, or to HEAD or @, gives [''] (the current branch, which the caller
 * resolves); --all, --mirror and globs give ['*'].
 */
export function pushTargets(command: string): string[] {
  return analyzePush(command).pushes.flatMap((p) => p.targets);
}

// ---------------------------------------------------------------------------
// Agent <-> hub messages (ExtMessage with ext 'landing')
// ---------------------------------------------------------------------------

/** hub -> agent 'prepare': a fresh checkout of the target with the change rebased onto it. */
export interface LandingPrepareRequest {
  /** Names the work folder (<agent home>/landing/work/<workId>). */
  workId: string;
  /** A checkout of the project on this machine: its origin is cloned (read-only use). */
  checkoutId: Id;
  /** The checkout that has the branch, when it is on this machine (fetched read-only). */
  sourceCheckoutId?: Id;
  branch: string;
  target: string;
}

export type LandingPrepareResult =
  | { ok: true; workDir: string; baseSha: string; headSha: string; sourceSha: string; commits: number; subject: string }
  | { ok: true; alreadyIn: true; workDir: string; baseSha: string; sourceSha: string }
  | { ok: false; conflict: true; files: string[]; baseSha: string; sourceSha: string; message: string }
  | { ok: false; conflict?: false; message: string };

/** hub -> agent 'push': push the verified commit to the target. Never forced. */
export interface LandingPushRequest {
  workId: string;
  target: string;
  sha: string;
}

export type LandingPushResult = { ok: true; sha: string } | { ok: false; rejected: boolean; message: string };

/** hub -> agent 'checkout': a fresh checkout of one commit, for a deploy. */
export interface LandingCheckoutRequest {
  workId: string;
  checkoutId: Id;
  sha: string;
}

export type LandingCheckoutResult = { ok: true; workDir: string } | { ok: false; message: string };

/** hub -> agent 'cleanup': remove a work folder. */
export interface LandingCleanupRequest {
  workId: string;
}

/** agent -> hub 'result' answers every request, with the request's id. */
export type LandingAgentResult = LandingPrepareResult | LandingPushResult | LandingCheckoutResult | { ok: boolean; message?: string };

// ---------------------------------------------------------------------------
// Hub <-> UI
// ---------------------------------------------------------------------------

/** state.ext.landing, GET /api/landing, and SSE ext 'landing' type 'state'. */
export interface LandingState {
  queues: LandingQueue[];
  /** Active, queued, parked and the recent finished ones. */
  landings: Landing[];
  configs: LandingProjectConfig[];
  /** Newest first, the recent ones. */
  deployments: Deployment[];
  at: Ts;
  /**
   * ADR 0066: for each project nobody configured, the verify command detected from its repository
   * (detectedVerifyCommand), which the queue uses. A project without one has no test command found.
   */
  detected?: Record<Id, { command: string; evidence: string }>;
  /** ADR 0077: the backend each project's default branch lands through, as last read. */
  backends?: Record<Id, LandingBackendChoice>;
}

/** Body of POST /api/landing/landings. */
export interface LandingSubmitRequest {
  projectId: Id;
  branch: string;
  /** Default: the project's default branch. */
  target?: string;
  title?: string;
  checkoutId?: Id;
  missionId?: Id;
  sessionId?: Id;
  /** ORGU-299: Watch incident linked to this landing. */
  incidentId?: Id;
}

/** Body of POST /api/landing/environments/:projectId/:env/(deploy|promote|rollback). */
export interface DeployRequest {
  /** deploy: the version (short sha) or a landing id to deploy. */
  version?: string;
  landingId?: Id;
  /** promote: the environment whose live version moves here. */
  from?: string;
  /** rollback: the version to go back to (default: the previous one). */
  to?: string;
  /** Protected environments: must equal the exact version being deployed. */
  confirm?: string;
}

/** Audit action names this module records (Audit page labels). */
export const LANDING_ACTION_LABEL: Record<string, string> = {
  'landing.submit': 'Submit to landing queue',
  'landing.cancel': 'Cancel landing',
  'landing.retry': 'Retry landing',
  'landing.reorder': 'Reorder landing queue',
  'landing.pause': 'Pause landing queue',
  'landing.resume': 'Resume landing queue',
  'landing.push': 'Push landed change',
  'landing.enqueue': "Enqueue on the host's merge queue",
  'landing.config': 'Change landing settings',
  'deploy.deploy': 'Deploy',
  'deploy.promote': 'Promote',
  'deploy.rollback': 'Roll back',
  'deploy.freeze': 'Add freeze window',
  'deploy.unfreeze': 'Remove freeze window',
};
